/**
 * @sazeo/sdk — Recruitment hooks
 *
 * Hooks that call the recruitment Edge Function handlers. All requests carry
 * the caller's Supabase JWT so RLS enforces tenant isolation automatically.
 *
 * Paired spec: specs/CODEX_RECRUITMENT_ADDON.md
 */

import { type TenantSupabaseClient, getTenantClient } from '@sazeo/supabase-tenant';
import { useCallback, useEffect, useRef, useState } from 'react';
import { buildFunctionUrl } from './functionUrl';

// ---------------------------------------------------------------------------
// Row types
// ---------------------------------------------------------------------------

export type EmploymentType = 'full_time' | 'part_time' | 'contract' | 'internship' | 'hourly';
export type PostingStatus =
  | 'draft'
  | 'open'
  | 'published'
  | 'paused'
  | 'closed'
  | 'archived'
  | 'cancelled';
export type ApplicationStage =
  | 'applied'
  | 'submitted'
  | 'screening'
  | 'waiting_cost_approval'
  | 'cost_estimated'
  | 'approved_for_ai'
  | 'ai_processing'
  | 'ai_done'
  | 'shortlisted'
  | 'interview'
  | 'offer'
  | 'hired'
  | 'rejected'
  | 'withdrawn';

const applicationStages: ApplicationStage[] = [
  'applied',
  'submitted',
  'screening',
  'waiting_cost_approval',
  'cost_estimated',
  'approved_for_ai',
  'ai_processing',
  'ai_done',
  'shortlisted',
  'interview',
  'offer',
  'hired',
  'rejected',
  'withdrawn',
];

export type PublicPostingStatus = PostingStatus | 'published' | 'paused' | 'archived';

export interface JobPosting {
  id: string;
  tenant_id: string;
  uid?: string | null;
  title: string;
  public_slug?: string | null;
  ai_requirements_summary?: string | null;
  department: string | null;
  location: string | null;
  city?: string | null;
  country?: string | null;
  employment_type: EmploymentType;
  experience_level?: string | null;
  work_arrangement?: 'onsite' | 'remote' | 'hybrid' | 'flexible' | null;
  description: string | null;
  requirements: string | null;
  responsibilities?: string | null;
  required_skills?: string[] | null;
  preferred_skills?: string[] | null;
  benefits?: string[] | null;
  salary_min: number | null;
  salary_max: number | null;
  currency: string;
  salary_currency?: string | null;
  salary_negotiable?: boolean | null;
  min_match_score?: number | null;
  anonymized_screening?: boolean | null;
  reveal_score_to_candidate?: boolean | null;
  status: PostingStatus;
  company_profile_id?: string | null;
  position_code?: string | null;
  manager_name?: string | null;
  hr_contact_name?: string | null;
  fields_data?: Record<string, unknown>;
  vacancy_language?: 'ka' | 'en' | 'both';
  application_limit?: number | null;
  ai_cv_autoparse?: boolean;
  auto_reject_enabled?: boolean;
  notify_on_application?: boolean;
  deadline_reminder?: boolean;
  published_at: string | null;
  closed_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface PublicJobPosting {
  id: string;
  title: string;
  department: string | null;
  location: string | null;
  city?: string | null;
  country?: string | null;
  employment_type: EmploymentType;
  work_arrangement?: 'onsite' | 'remote' | 'hybrid' | 'flexible' | null;
  description: string | null;
  requirements: string | null;
  responsibilities?: string | null;
  required_skills?: string[] | null;
  preferred_skills?: string[] | null;
  benefits?: string[] | null;
  salary_min: number | null;
  salary_max: number | null;
  currency: string;
  salary_currency?: string | null;
  salary_negotiable?: boolean | null;
  status: PublicPostingStatus;
  public_slug: string;
  published_at: string | null;
  expires_at?: string | null;
}

export interface PublicApplicationStatus {
  application_id: string;
  public_token: string;
  stage: string;
  submitted_at: string;
  vacancy_title: string;
  vacancy_slug: string;
}

export class RecruitmentPublicApiError extends Error {
  status: number;
  requestId?: string;

  constructor(message: string, status: number, requestId?: string) {
    super(message);
    this.name = 'RecruitmentPublicApiError';
    this.status = status;
    this.requestId = requestId;
  }
}

export class RecruitmentApiError extends Error {
  status: number;
  code?: string;
  requestId?: string;

  constructor(message: string, status: number, code?: string, requestId?: string) {
    super(message);
    this.name = 'RecruitmentApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

function makeRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function makePublicRequestHeaders(): Headers {
  const headers = new Headers();
  headers.set('x-request-id', makeRequestId());
  const anonKey =
    (import.meta.env?.VITE_SUPABASE_ANON_KEY as string | undefined) ??
    (typeof globalThis !== 'undefined'
      ? (globalThis as { __SAZEO_VITE_ENV__?: { VITE_SUPABASE_ANON_KEY?: string } })
          .__SAZEO_VITE_ENV__?.VITE_SUPABASE_ANON_KEY
      : undefined);
  if (anonKey) {
    headers.set('apikey', anonKey);
    if (anonKey.split('.').length === 3) {
      headers.set('authorization', `Bearer ${anonKey}`);
    }
  }
  return headers;
}

async function readPublicError(res: Response): Promise<RecruitmentPublicApiError> {
  const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
    error?: { message?: string; request_id?: string };
  };
  const requestId = res.headers.get('x-request-id') ?? body.error?.request_id ?? undefined;
  return new RecruitmentPublicApiError(
    body.error?.message ?? res.statusText,
    res.status,
    requestId,
  );
}

async function readRecruitmentError(res: Response): Promise<RecruitmentApiError> {
  const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
    error?: { code?: string; message?: string; request_id?: string };
  };
  return new RecruitmentApiError(
    body.error?.message ?? res.statusText,
    res.status,
    body.error?.code,
    res.headers.get('x-request-id') ?? body.error?.request_id ?? undefined,
  );
}

export interface JobApplication {
  id: string;
  tenant_id: string;
  job_posting_id: string;
  user_id: string | null;
  applicant_name: string;
  applicant_email: string;
  applicant_phone?: string | null;
  resume_url: string | null;
  cover_letter: string | null;
  stage: ApplicationStage;
  source?: string | null;
  ai_score?: number | null;
  ai_summary?: string | null;
  ai_parsed_data?: Record<string, unknown> | null;
  rejection_reason: string | null;
  notes: string | null;
  assigned_to: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface JobPostingFilters {
  status?: PostingStatus;
}

export interface ApplicationFilters {
  postingId?: string;
  stage?: ApplicationStage;
}

export interface CreateJobPostingInput {
  title: string;
  company_profile_id?: string | null;
  position_code?: string | null;
  city?: string | null;
  country?: string | null;
  department?: string | null;
  location?: string | null;
  employment_type?: EmploymentType;
  experience_level?: string | null;
  work_arrangement?: 'onsite' | 'remote' | 'hybrid' | 'flexible' | null;
  manager_name?: string | null;
  hr_contact_name?: string | null;
  description?: string | null;
  requirements?: string | null;
  responsibilities?: string | null;
  required_skills?: string[];
  preferred_skills?: string[];
  benefits?: string[];
  salary_min?: number | null;
  salary_max?: number | null;
  currency?: string;
  salary_currency?: string;
  salary_negotiable?: boolean;
  min_match_score?: number;
  anonymized_screening?: boolean;
  reveal_score_to_candidate?: boolean;
  status?: PostingStatus;
  fields_data?: Record<string, unknown>;
  vacancy_language?: 'ka' | 'en' | 'both';
  application_limit?: number | null;
  ai_cv_autoparse?: boolean;
  auto_reject_enabled?: boolean;
  notify_on_application?: boolean;
  deadline_reminder?: boolean;
}

export type UpdateJobPostingInput = Partial<CreateJobPostingInput>;

export interface CreateApplicationInput {
  applicant_name: string;
  applicant_email: string;
  resume_url?: string | null;
  cover_letter?: string | null;
}

export interface UpdateApplicationStageInput {
  stage: ApplicationStage;
  rejectionReason?: string | null;
  notes?: string | null;
}

export interface HrInterview {
  id: string;
  tenant_id: string;
  application_id: string;
  scheduled_at: string;
  duration_min: number;
  type: 'online' | 'office';
  meeting_link: string | null;
  location: string | null;
  status: 'pending' | 'confirmed' | 'rescheduled' | 'cancelled';
  channels: Array<'email' | 'sms' | 'whatsapp'>;
  confirm_token: string;
  notes: string | null;
  created_at: string;
}

export interface ScheduleInterviewInput {
  applicationId: string;
  tenantId: string;
  scheduled_at: string;
  duration_min?: number;
  type?: 'online' | 'office';
  meeting_link?: string | null;
  location?: string | null;
  channels: Array<'email' | 'sms' | 'whatsapp'>;
  notes?: string | null;
}

export interface SendRejectionInput {
  applicationIds: string[];
  tenantId: string;
  channel: 'email' | 'sms' | 'whatsapp';
  message?: string | null;
}

export interface AiProcessCvInput {
  applicationId: string;
  tenantId: string;
}

export interface AiProcessCvResult {
  ai_score: number;
  ai_summary: string;
  ai_parsed_data: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Internal fetch helpers (same pattern as expenses.ts)
// ---------------------------------------------------------------------------

interface SupabaseAuthLike {
  auth: {
    getSession: () => Promise<{ data: { session: { access_token: string } | null } }>;
  };
}

function resolveClient(supabase: unknown): SupabaseAuthLike {
  if (supabase) return supabase as SupabaseAuthLike;
  try {
    return getTenantClient() as unknown as TenantSupabaseClient as unknown as SupabaseAuthLike;
  } catch {
    return { auth: { getSession: async () => ({ data: { session: null } }) } };
  }
}

async function getAuthHeaders(supabase: unknown): Promise<Record<string, string>> {
  const client = resolveClient(supabase);
  const { data } = await client.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(supabase: unknown, path: string, init?: RequestInit): Promise<T> {
  const authHeaders = await getAuthHeaders(supabase);
  const headers = new Headers(init?.headers);
  headers.set('content-type', headers.get('content-type') ?? 'application/json');
  headers.set('x-request-id', headers.get('x-request-id') ?? makeRequestId());
  for (const [key, value] of Object.entries(authHeaders)) {
    headers.set(key, value);
  }
  const res = await fetch(buildFunctionUrl(path), {
    ...init,
    headers,
  });
  if (!res.ok) {
    throw await readRecruitmentError(res);
  }
  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}

function useAsyncQuery<T>(
  fn: () => Promise<T>,
  initial: T,
  deps: readonly unknown[],
  enabled = true,
): { data: T; isLoading: boolean; error: Error | null; refetch: () => void } {
  const [data, setData] = useState<T>(initial);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: callers provide the explicit query key deps.
  const run = useCallback(() => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    fn()
      .then((d) => {
        if (mountedRef.current) setData(d);
      })
      .catch((e) => {
        if (mountedRef.current) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
  }, deps);

  // biome-ignore lint/correctness/useExhaustiveDependencies: callers provide the explicit query key deps.
  useEffect(() => {
    run();
  }, deps);

  return { data, isLoading, error, refetch: run };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useJobPostings(supabase: unknown, tenantId: string, filters?: JobPostingFilters) {
  const params = new URLSearchParams({ tenant_id: tenantId });
  if (filters?.status) params.set('status', filters.status);

  return useAsyncQuery<JobPosting[]>(
    () => apiFetch<JobPosting[]>(supabase, `addons/recruitment/postings?${params.toString()}`),
    [],
    [tenantId, filters?.status ?? null] as readonly unknown[],
    Boolean(tenantId),
  );
}

export function useJobPosting(supabase: unknown, tenantId: string, postingId: string) {
  return useAsyncQuery<JobPosting | null>(
    () =>
      apiFetch<JobPosting>(
        supabase,
        `addons/recruitment/postings/${postingId}?tenant_id=${tenantId}`,
      ),
    null,
    [tenantId, postingId] as readonly unknown[],
    Boolean(tenantId) && Boolean(postingId),
  );
}

export function useApplications(supabase: unknown, tenantId: string, filters?: ApplicationFilters) {
  const params = new URLSearchParams({ tenant_id: tenantId });
  if (filters?.postingId) params.set('posting_id', filters.postingId);
  if (filters?.stage) params.set('stage', filters.stage);

  return useAsyncQuery<JobApplication[]>(
    () =>
      apiFetch<JobApplication[]>(supabase, `addons/recruitment/applications?${params.toString()}`),
    [],
    [tenantId, filters?.postingId ?? null, filters?.stage ?? null] as readonly unknown[],
    Boolean(tenantId),
  );
}

export function useApplicationsByStage(
  supabase: unknown,
  tenantId: string,
  postingId: string,
): {
  data: Record<ApplicationStage, JobApplication[]>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const query = useApplications(supabase, tenantId, { postingId });
  const grouped = applicationStages.reduce(
    (acc, stage) => {
      acc[stage] = [];
      return acc;
    },
    {} as Record<ApplicationStage, JobApplication[]>,
  );
  for (const app of query.data) {
    grouped[app.stage].push(app);
  }
  return { ...query, data: grouped };
}

export function useCreateJobPosting(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string } & CreateJobPostingInput) => {
      setIsPending(true);
      setError(null);
      try {
        const { tenantId, ...payload } = input;
        return await apiFetch<JobPosting>(supabase, 'addons/recruitment/postings', {
          method: 'POST',
          body: JSON.stringify({ tenant_id: tenantId, ...payload }),
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [supabase],
  );

  return { mutate, isPending, error };
}

export function useUpdateJobPosting(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string; postingId: string } & UpdateJobPostingInput) => {
      setIsPending(true);
      setError(null);
      try {
        const { postingId, tenantId, ...payload } = input;
        return await apiFetch<JobPosting>(supabase, `addons/recruitment/postings/${postingId}`, {
          method: 'PATCH',
          body: JSON.stringify({ tenant_id: tenantId, ...payload }),
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [supabase],
  );

  return { mutate, isPending, error };
}

export function useUpdateJobPostingStatus(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string; postingId: string; status: PostingStatus }) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<JobPosting>(
          supabase,
          `addons/recruitment/postings/${input.postingId}/status`,
          {
            method: 'PATCH',
            body: JSON.stringify({ tenant_id: input.tenantId, status: input.status }),
          },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [supabase],
  );

  return { mutate, isPending, error };
}

export function useCreateApplication(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string; postingId: string } & CreateApplicationInput) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<JobApplication>(
          supabase,
          `addons/recruitment/postings/${input.postingId}/applications`,
          {
            method: 'POST',
            body: JSON.stringify(input),
          },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [supabase],
  );

  return { mutate, isPending, error };
}

export function useUpdateApplicationStage(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string; applicationId: string } & UpdateApplicationStageInput) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<JobApplication>(
          supabase,
          `addons/recruitment/applications/${input.applicationId}/stage`,
          {
            method: 'PATCH',
            body: JSON.stringify({
              tenant_id: input.tenantId,
              stage: input.stage,
              rejection_reason: input.rejectionReason ?? null,
              notes: input.notes ?? null,
            }),
          },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [supabase],
  );

  return { mutate, isPending, error };
}

export interface SendApplicationOfferInput {
  position: string;
  start_date?: string | null;
  salary?: string | null;
}

export function useSendApplicationOffer(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string; applicationId: string } & SendApplicationOfferInput) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<{ contract: { id: string }; application_id: string }>(
          supabase,
          `addons/recruitment/applications/${input.applicationId}/offer`,
          {
            method: 'POST',
            body: JSON.stringify({
              position: input.position,
              start_date: input.start_date ?? null,
              salary: input.salary ?? null,
            }),
          },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [supabase],
  );

  return { mutate, isPending, error };
}

export function useScheduleInterview(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: ScheduleInterviewInput) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<HrInterview>(supabase, 'addons/recruitment/interviews', {
          method: 'POST',
          body: JSON.stringify(input),
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [supabase],
  );

  return { mutate, isPending, error };
}

export function useInterviews(supabase: unknown, tenantId: string, applicationId: string) {
  return useAsyncQuery<HrInterview[]>(
    () =>
      apiFetch<HrInterview[]>(
        supabase,
        `addons/recruitment/applications/${applicationId}/interviews?tenant_id=${tenantId}`,
      ),
    [],
    [tenantId, applicationId] as readonly unknown[],
    Boolean(tenantId) && Boolean(applicationId),
  );
}

export function useSendRejection(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: SendRejectionInput) => {
      setIsPending(true);
      setError(null);
      try {
        await apiFetch<void>(supabase, 'addons/recruitment/rejections', {
          method: 'POST',
          body: JSON.stringify(input),
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [supabase],
  );

  return { mutate, isPending, error };
}

export function useAiProcessCv(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: AiProcessCvInput) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<AiProcessCvResult>(
          supabase,
          `addons/recruitment/applications/${input.applicationId}/ai-process`,
          {
            method: 'POST',
            body: JSON.stringify({ tenantId: input.tenantId }),
          },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [supabase],
  );

  return { mutate, isPending, error };
}

// ---------------------------------------------------------------------------
// Candidate Premium Jobs (pre-check + tailoring)
// ---------------------------------------------------------------------------

export interface LegacyCandidatePremiumJob {
  id: string;
  job_type: 'precheck' | 'tailoring';
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  result_payload: {
    kind?: 'candidate_precheck_result' | 'candidate_tailoring_result';
    match_score?: number;
    missing_skills?: string[];
    suggestions?: string[];
    honest_advice?: string;
    files?: { url: string; filename: string; mime_type: string }[];
    original_text?: string;
    tailored_text?: string;
    changes?: string[];
    warnings?: string[];
  };
  input_payload: {
    vacancy_title?: string;
    candidate_premium?: { product: string; source_job_slug?: string };
  };
  amount_minor: number;
  currency: string;
  created_at: string;
  completed_at: string | null;
  error_message: string | null;
}

export function useLegacyCandidatePremiumJob(
  supabase: unknown,
  id: string | null,
  options?: { enabled?: boolean },
): { job: LegacyCandidatePremiumJob | null; loading: boolean; error: Error | null } {
  const enabled = options?.enabled ?? Boolean(id);
  const result = useAsyncQuery<LegacyCandidatePremiumJob | null>(
    () =>
      apiFetch<LegacyCandidatePremiumJob>(supabase, `addons/candidate-premium/jobs/${id ?? ''}`),
    null,
    [id] as readonly unknown[],
    enabled && Boolean(id),
  );
  return { job: result.data, loading: result.isLoading, error: result.error };
}

export async function submitPublicApplication(
  slug: string,
  formData: FormData,
): Promise<{
  application_id: string;
  public_token: string | null;
  status_url: string | null;
  message: string;
}> {
  const res = await fetch(
    `${buildFunctionUrl('')}addons/recruitment/public/apply/${encodeURIComponent(slug)}`,
    {
      method: 'POST',
      headers: makePublicRequestHeaders(),
      body: formData,
    },
  );
  if (!res.ok) {
    throw await readPublicError(res);
  }
  return res.json() as Promise<{
    application_id: string;
    public_token: string | null;
    status_url: string | null;
    message: string;
  }>;
}

export async function getPublicJobPosting(slug: string): Promise<PublicJobPosting> {
  const res = await fetch(
    `${buildFunctionUrl('')}addons/recruitment/public/jobs/${encodeURIComponent(slug)}`,
    { headers: makePublicRequestHeaders() },
  );
  if (!res.ok) {
    throw await readPublicError(res);
  }
  return res.json() as Promise<PublicJobPosting>;
}

export async function getPublicApplicationStatus(token: string): Promise<PublicApplicationStatus> {
  const res = await fetch(
    `${buildFunctionUrl('')}addons/recruitment/public/applications/${encodeURIComponent(
      token,
    )}/status`,
    { headers: makePublicRequestHeaders() },
  );
  if (!res.ok) {
    throw await readPublicError(res);
  }
  return res.json() as Promise<PublicApplicationStatus>;
}
